import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { styled } from "@mui/material/styles";
import { toast } from "react-toastify";
import DeleteConfirmationModal from "../deleteConfirmationModal";
import { generatedSalesCartService } from "../../utils/services/generated-sales-cart.service";
import { formatDate } from "../../utils/formatDate";

const StyledTableCell = styled(TableCell)(() => ({
  "&.MuiTableCell-head": {
    backgroundColor: "var(--table-header-bg)",
    color: "var(--text-primary)",
    padding: "10px 12px",
    borderBottom: "1px solid var(--border-color)",
  },
  "&.MuiTableCell-body": {
    color: "var(--text-primary)",
    padding: "10px 12px",
    borderBottom: "1px solid var(--border-color)",
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  "&:nth-of-type(odd)": {
    backgroundColor: "var(--row-bg-odd)",
  },
  "&:nth-of-type(even)": {
    backgroundColor: "var(--row-bg-even)",
  },
}));

const ActionMenu = ({ row, onEdit, onDelete }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  return (
    <>
      <IconButton onClick={(event) => setAnchorEl(event.currentTarget)}>
        <MoreVertIcon sx={{ color: "var(--text-secondary)" }} />
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            backgroundColor: "var(--bg-paper)",
            border: "1px solid var(--border-color)",
            minWidth: 140,
            p: 1,
          },
        }}
      >
        <Button
          fullWidth
          onClick={() => {
            setAnchorEl(null);
            onEdit(row);
          }}
          sx={{ justifyContent: "flex-start", textTransform: "none", color: "var(--text-primary)" }}
        >
          Edit
        </Button>
        <Button
          fullWidth
          onClick={() => {
            setAnchorEl(null);
            onDelete(row);
          }}
          sx={{ justifyContent: "flex-start", textTransform: "none", color: "error.main" }}
        >
          Delete
        </Button>
      </Popover>
    </>
  );
};

const formatCurrency = (value) => {
  const numeric = Number(value || 0);
  return numeric.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatMonthYear = (month, year) => `${month}/${year}`;

const getCartTotal = (items = []) =>
  items.reduce(
    (total, item) => total + Number(item.quantity || 0) * Number(item.price || 0),
    0,
  );

const GeneratedSalesCartList = ({ data, onEditData, onDeleteSuccess }) => {
  const [selectedCart, setSelectedCart] = useState(null);
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const rows = useMemo(() => data || [], [data]);

  const handleDelete = async () => {
    if (!selectedCart?._id) {
      return;
    }

    try {
      await generatedSalesCartService.delete(selectedCart._id);
      toast.success("Generated sales cart deleted successfully.");
      setOpenConfirmation(false);
      setSelectedCart(null);
      onDeleteSuccess?.();
    } catch (error) {
      console.error("Failed to delete generated sales cart", error);
    }
  };

  return (
    <>
      <TableContainer
        component={Paper}
        sx={{
          boxShadow: "none",
          borderRadius: 3,
          border: "1px solid var(--border-color)",
          backgroundColor: "var(--bg-paper)",
          mb: 10,
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <StyledTableCell>Customer</StyledTableCell>
              <StyledTableCell>Period</StyledTableCell>
              <StyledTableCell>Status</StyledTableCell>
              <StyledTableCell>Items</StyledTableCell>
              <StyledTableCell>Total</StyledTableCell>
              <StyledTableCell>Updated</StyledTableCell>
              <StyledTableCell align="right">Actions</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <StyledTableRow key={row._id}>
                <StyledTableCell>
                  <Typography fontWeight={500} color="var(--text-primary)">
                    {row.customer}
                  </Typography>
                </StyledTableCell>
                <StyledTableCell>{formatMonthYear(row.month, row.year)}</StyledTableCell>
                <StyledTableCell>
                  <Chip
                    label={row.status || "generated"}
                    size="small"
                    sx={{
                      textTransform: "capitalize",
                      backgroundColor: "var(--bg-secondary)",
                      color: "var(--text-primary)",
                      border: "1px solid var(--border-color)",
                    }}
                  />
                </StyledTableCell>
                <StyledTableCell>
                  <Box>
                    <Typography fontWeight={600} color="var(--text-primary)">
                      {row.items?.length || 0} item{row.items?.length === 1 ? "" : "s"}
                    </Typography>
                    <Typography fontSize={12} color="var(--text-secondary)">
                      {(row.items || []).slice(0, 2).map((item) => item.sku).join(", ") || "No SKU"}
                      {row.items?.length > 2 ? "..." : ""}
                    </Typography>
                  </Box>
                </StyledTableCell>
                <StyledTableCell>{formatCurrency(getCartTotal(row.items))}</StyledTableCell>
                <StyledTableCell>
                  {row.updatedAt ? formatDate(row.updatedAt) : "-"}
                </StyledTableCell>
                <StyledTableCell align="right">
                  <ActionMenu
                    row={row}
                    onEdit={onEditData}
                    onDelete={(cart) => {
                      setSelectedCart(cart);
                      setOpenConfirmation(true);
                    }}
                  />
                </StyledTableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <DeleteConfirmationModal
        open={openConfirmation}
        handleClose={() => {
          setOpenConfirmation(false);
          setSelectedCart(null);
        }}
        handleDelete={handleDelete}
        title="Delete generated sales cart?"
        deleteButtonTitle="Delete"
      />
    </>
  );
};

export default GeneratedSalesCartList;
